import React, { useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { HELP_PAGE_LINK } from "../../constants";
import { altAttribute } from "../../utils/utils";
import auth from "../../services/auth";
import { v2Fonts } from "../theme/v2Theme";

function getTimeGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
}

const TopNav = ({
  greetingName = "there",
  fullName,
  avatarUrl,
  userId,
  navExpanded = true,
  onNavToggle,
}) => {
  const displayName = fullName || greetingName;
  const timeGreeting = getTimeGreeting();
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    auth.logout(() => {
      window.location.href = "/";
    });
  };

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        bgcolor: "#FFFFFF",
        backgroundImage: "none",
        color: "#2C2C2A",
        borderBottom: "1px solid #E8E6E1",
        boxShadow: "none",
      }}
    >
      <Toolbar
        disableGutters
        sx={{
          minHeight: "58px !important",
          height: 58,
          maxHeight: 58,
          px: "22px",
          py: "13px",
          boxSizing: "border-box",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
          bgcolor: "#FFFFFF",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            p: 0,
            gap: "10px",
            height: 32,
            minWidth: 0,
          }}
        >
          <IconButton
            type="button"
            className="edsimsV2-nav-toggle"
            aria-label={
              navExpanded ? "Collapse navigation" : "Expand navigation"
            }
            aria-expanded={navExpanded}
            onClick={onNavToggle}
            size="small"
          >
            <i className="fas fa-bars" aria-hidden="true" />
          </IconButton>

          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              p: 0,
              height: 15,
              minWidth: 0,
            }}
          >
            <Typography
              component="span"
              sx={{
                fontFamily: v2Fonts.inter,
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: "13.5px",
                lineHeight: "100%",
                display: "flex",
                alignItems: "center",
                color: "#888780",
              }}
            >
              {timeGreeting},&nbsp;
            </Typography>
            <Typography
              component="span"
              noWrap
              sx={{
                fontFamily: v2Fonts.inter,
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: "13.5px",
                lineHeight: "100%",
                display: "flex",
                alignItems: "center",
                color: "#2C2C2A",
              }}
            >
              {greetingName}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "flex-end",
            alignItems: "center",
            p: 0,
            gap: "12px",
            height: 32,
            flexShrink: 0,
          }}
        >
          <IconButton
            component="a"
            href={HELP_PAGE_LINK}
            target="_blank"
            rel="noopener noreferrer"
            size="small"
            aria-label="Help and documentation (opens in a new tab)"
            sx={{
              width: 32,
              height: 32,
              p: 0,
              bgcolor: "#FFFFFF",
              border: "1px solid #D8D8D8",
              borderRadius: "200px",
              color: "#6D6D6D",
              "&:hover": { bgcolor: "#FFFFFF" },
            }}
          >
            <Box
              component="i"
              className="far fa-question-circle"
              aria-hidden="true"
              sx={{ fontSize: "14px", width: 16, height: 16, lineHeight: "16px" }}
            />
          </IconButton>

          <IconButton
            size="small"
            aria-label="Notifications"
            sx={{
              width: 32,
              height: 32,
              p: 0,
              bgcolor: "#FFFFFF",
              border: "1px solid #D8D8D8",
              borderRadius: "200px",
              color: "#6D6D6D",
              "&:hover": { bgcolor: "#FFFFFF" },
            }}
          >
            <Box
              component="i"
              className="far fa-bell"
              aria-hidden="true"
              sx={{ fontSize: "14px", width: 16, height: 16, lineHeight: "16px" }}
            />
          </IconButton>

          <Box
            component="button"
            id="topnav-profile-button"
            type="button"
            onClick={handleMenuOpen}
            aria-label="Account menu"
            aria-controls={menuOpen ? "topnav-profile-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={menuOpen ? "true" : undefined}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              px: "8px",
              py: 0,
              gap: "8px",
              height: 32,
              border: "none",
              background: "transparent",
              cursor: "pointer",
              font: "inherit",
              color: "inherit",
              borderRadius: "8px",
              "&:hover": { bgcolor: "#F5F5F5" },
            }}
          >
            <Avatar
              src={avatarUrl}
              alt={altAttribute.profileAlt}
              sx={{
                width: 32,
                height: 32,
                bgcolor: "#F7F3FF",
                color: "#5941D5",
                fontSize: "12px",
                fontWeight: 700,
                fontFamily: v2Fonts.inter,
              }}
            >
              {greetingName.charAt(0).toUpperCase()}
            </Avatar>
            <Typography
              noWrap
              sx={{
                display: { xs: "none", sm: "flex" },
                alignItems: "center",
                maxWidth: 140,
                height: 12,
                fontFamily: v2Fonts.inter,
                fontStyle: "normal",
                fontWeight: 400,
                fontSize: "12px",
                lineHeight: "100%",
                color: "#787880",
              }}
            >
              {displayName}
            </Typography>
            <Box
              component="i"
              className="fas fa-chevron-down"
              aria-hidden="true"
              sx={{
                width: 16,
                height: 16,
                fontSize: "10px",
                color: "#A29CB1",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transform: menuOpen ? "rotate(180deg)" : "none",
                transition: "transform 0.15s ease",
              }}
            />
          </Box>
          <Menu
            id="topnav-profile-menu"
            anchorEl={anchorEl}
            open={menuOpen}
            onClose={handleMenuClose}
            keepMounted
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            slotProps={{
              paper: {
                sx: {
                  mt: 1,
                  minWidth: 150,
                  borderRadius: "8px",
                  boxShadow: "0 4px 16px rgba(38, 33, 92, 0.12)",
                },
              },
              list: {
                "aria-labelledby": "topnav-profile-button",
              },
            }}
          >
            <MenuItem
              component={userId ? RouterLink : "li"}
              to={userId ? `/users/profile/${userId}` : undefined}
              disabled={!userId}
              onClick={handleMenuClose}
              sx={{
                minWidth: 150,
                fontFamily: v2Fonts.inter,
                fontSize: "13px",
                color: "#2A2A2C",
                "&:hover": { color: "#5941D5", bgcolor: "#F7F3FF" },
              }}
            >
              Profile
            </MenuItem>
            <MenuItem
              onClick={handleLogout}
              sx={{
                fontFamily: v2Fonts.inter,
                fontSize: "13px",
                color: "#2A2A2C",
                "&:hover": { color: "#AD3030", bgcolor: "#FDF2F2" },
              }}
            >
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default TopNav;
